import { EFinancingProgram } from '@if/core/types'
import { getApiClient } from '@src/services/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CreditApplication, CreditTaskDashboardEntry, FullCreditApplication } from '../../types'
import { transformCreditApplicationFromApi } from '../credit-application/credit-transform'
import { creditTaskDashboardKeys } from '../credit-task-dashboard-entry/credit-task-DashboardEntries-api'
import { fullCreditApplicationKeys } from '../full-credit-application/full-credit-application-api'

// ==================== TYPES ====================

export interface AssignableFollowUpAgent {
  id: string
  firstName: string
  lastName: string
  initials: string
  email: string
}

export interface FollowUpAgent {
  id: string
  firstName: string
  lastName: string
  initials: string
}

export interface AssignFollowUpAgentDto {
  agentId: string
}

// ==================== KEY FACTORY ====================

const SCOPE = 'follow-up-agent'
const ASSIGNABLE = 'assignable'

const keysFactory = {
  all: [SCOPE] as const,
  assignable: () => [SCOPE, ASSIGNABLE] as const,
}

// ==================== API FUNCTIONS ====================

/**
 * Fetches the list of users who can be assigned as follow-up agents
 */
const getAssignableAgentsApi = async (): Promise<AssignableFollowUpAgent[]> => {
  const response = await getApiClient().get<AssignableFollowUpAgent[]>('Users/AssignableFollowUpAgents')
  return response.data
}

/**
 * Hook to fetch assignable follow-up agents
 * @param enabled Whether the query should be enabled (default: false)
 */
export function useGetAssignableAgents(enabled = false): [AssignableFollowUpAgent[], boolean, unknown] {
  const { data, isFetching, error } = useQuery({
    queryKey: keysFactory.assignable(),
    queryFn: getAssignableAgentsApi,
    enabled,
    staleTime: 60 * 60 * 1000, // Cache for 1 hour - agents list changes rarely
    gcTime: 120 * 60 * 1000, // Keep in garbage collection for 2 hours
  })

  return [data ?? [], isFetching, error]
}

/**
 * Assigns a follow-up agent to a credit application
 * Returns the full updated credit application (same pattern as UpdateIncomes)
 */
const assignFollowUpAgentApi = async (
  _financingProgramId: string,
  creditApplicationId: string,
  dto: AssignFollowUpAgentDto,
): Promise<CreditApplication> => {
  const response = await getApiClient().post<CreditApplication>(
    `Applications/${encodeURIComponent(creditApplicationId)}/AssignFollowUpAgent`,
    dto,
  )
  return transformCreditApplicationFromApi(response.data)
}

/**
 * Hook to assign a follow-up agent
 * Follows the same pattern as useUpdateIncomes - returns full CreditApplication
 * @param financingProgramId The financing program ID
 * @param creditApplicationId The credit application ID
 * @param currentFilters Optional filters to invalidate specific dashboard queries
 */
export function useAssignFollowUpAgent(
  financingProgramId: string,
  creditApplicationId: string,
  currentFilters?: Record<string, string | number | string[] | number[]>,
): [(dto: AssignFollowUpAgentDto) => Promise<CreditApplication>, boolean, unknown] {
  const queryClient = useQueryClient()

  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: (dto: AssignFollowUpAgentDto) =>
      assignFollowUpAgentApi(financingProgramId, creditApplicationId, dto),
    onSuccess: (updatedCredit) => {
      // Update full credit application cache
      const fullCreditAppKey = fullCreditApplicationKeys.detail(creditApplicationId, financingProgramId as EFinancingProgram)
      const currentFullCreditApp = queryClient.getQueryData<FullCreditApplication>(fullCreditAppKey)
      if (currentFullCreditApp) {
        queryClient.setQueryData<FullCreditApplication>(fullCreditAppKey, {
          ...currentFullCreditApp,
          credit: updatedCredit,
        })
      }

      // Update dashboard entries cache
      if (currentFilters) {
        const dashboardKey = creditTaskDashboardKeys.list(currentFilters)
        const currentEntries = queryClient.getQueryData<CreditTaskDashboardEntry[]>(dashboardKey)
        if (currentEntries) {
          queryClient.setQueryData<CreditTaskDashboardEntry[]>(
            dashboardKey,
            currentEntries.map((entry) =>
              entry.id === creditApplicationId
                ? { ...entry, followUpAgent: updatedCredit.followUpAgent }
                : entry,
            ),
          )
        }
      }
    },
  })

  return [mutateAsync, isPending, error]
}

/**
 * Unassigns the follow-up agent from a credit application
 * Returns the full updated credit application (same pattern as AssignFollowUpAgent)
 */
const unassignFollowUpAgentApi = async (
  _financingProgramId: string,
  creditApplicationId: string,
): Promise<CreditApplication> => {
  const response = await getApiClient().post<CreditApplication>(
    `Applications/${encodeURIComponent(creditApplicationId)}/UnassignFollowUpAgent`,
  )
  return transformCreditApplicationFromApi(response.data)
}

/**
 * Hook to unassign a follow-up agent
 * Follows the same pattern as useAssignFollowUpAgent - returns full CreditApplication
 * Updates both full application and dashboard caches on success
 * @param financingProgramId The financing program ID
 * @param creditApplicationId The credit application ID
 * @param currentFilters Optional filters to invalidate specific dashboard queries
 */
export function useUnassignFollowUpAgent(
  financingProgramId: string,
  creditApplicationId: string,
  currentFilters?: Record<string, string | number | string[] | number[]>,
): [() => Promise<CreditApplication>, boolean, unknown] {
  const queryClient = useQueryClient()

  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: () => unassignFollowUpAgentApi(financingProgramId, creditApplicationId),
    onSuccess: (updatedCredit) => {
      // Update full credit application cache
      const fullCreditAppKey = fullCreditApplicationKeys.detail(creditApplicationId, financingProgramId as EFinancingProgram)
      const currentFullCreditApp = queryClient.getQueryData<FullCreditApplication>(fullCreditAppKey)
      if (currentFullCreditApp) {
        queryClient.setQueryData<FullCreditApplication>(fullCreditAppKey, {
          ...currentFullCreditApp,
          credit: updatedCredit,
        })
      }

      // Update dashboard entries cache
      if (currentFilters) {
        const dashboardKey = creditTaskDashboardKeys.list(currentFilters)
        const currentEntries = queryClient.getQueryData<CreditTaskDashboardEntry[]>(dashboardKey)
        if (currentEntries) {
          queryClient.setQueryData<CreditTaskDashboardEntry[]>(
            dashboardKey,
            currentEntries.map((entry) =>
              entry.id === creditApplicationId
                ? { ...entry, followUpAgent: updatedCredit.followUpAgent }
                : entry,
            ),
          )
        }
      }
    },
  })

  return [mutateAsync, isPending, error]
}
